import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { Message } from '@chatapp-inbox/shared';
import { markRead } from '../../api';
import { syncConversation } from '../conversation';

interface Params {
  conversationId: string;
  canWrite: boolean;
  unreadCount: number;
  /** O histórico já carregou? Marcar "até a última recebida" exige saber qual ela é. */
  loaded: boolean;
  messages: readonly Message[];
}

/**
 * Marca a conversa como lida enquanto ela está aberta e À VISTA. Só quem pode escrever marca
 * (quem apenas lê não mexe nas não lidas de ninguém) e só "até a última mensagem recebida" que a
 * tela já tem: o que chegar depois continua não lido até a pessoa vê-lo.
 *
 * Uma chamada por vez, sem laço: depois do sucesso as não lidas viram 0 (ou o que chegou no meio
 * do caminho) e o efeito se desarma sozinho. Falha é silenciosa e só recomeça no próximo gatilho:
 * a aba voltar a ficar visível, a janela ganhar foco ou chegar outra mensagem recebida.
 */
export function useMarkRead({
  conversationId,
  canWrite,
  unreadCount,
  loaded,
  messages,
}: Params): void {
  const queryClient = useQueryClient();
  const inFlight = useRef(false);
  // Muda quando a aba volta a ser visível ou a janela ganha foco: é o "tentar de novo" da falha.
  const [wake, setWake] = useState(0);

  useEffect(() => {
    const onWake = (): void => setWake((count) => count + 1);
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, []);

  let upTo: string | null = null;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.direction === 'in') {
      upTo = messages[i]!.id;
      break;
    }
  }

  useEffect(() => {
    if (!canWrite || unreadCount <= 0 || !loaded || upTo === null) return;
    if (document.visibilityState !== 'visible') return;
    if (inFlight.current) return;
    inFlight.current = true;
    markRead(conversationId, upTo)
      .then((conversation) => syncConversation(queryClient, conversation))
      .catch(() => {
        /* silencioso: o próximo gatilho tenta de novo */
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [canWrite, unreadCount, loaded, upTo, wake, conversationId, queryClient]);
}
